import { parsePhoneNumberFromString } from 'libphonenumber-js';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
  addToCart,
  updateSMSNumberConfirmed,
} from '../../../../actions/v3Order';
import formatUSD from '../../../../utils/formatUSD';
import { isJpgImage } from '../../../../utils/imageHelpers';
import {
  isProductAllowed,
  isShippingRestrictedForParent,
} from '../../helpers/internationalRestriction';
import SMSNumberConfirmation from '../regular/subcomponents/SMSNumberConfirmation';
import ProductOptionsSelector, {
  getProductOptionsDefaultsKey,
  reconcileProductOptions,
} from '../regular/subcomponents/ProductOptionsSelector';
import CropOptionsPrepaySelector from '../regular/subcomponents/CropOptionsPrepaySelector';
import MultiConfigProductItem from './MultiConfigProductItem';
import {
  MultiConfigButton,
  MultiConfigGrid,
  MultiConfigGridItem,
  MultiConfigHeader,
  MultiConfigSubHeader,
  ProductItemButton,
} from './multiConfigStyles';
import { useForm } from 'react-hook-form';
import RequiredProductForm from '../required-product-form/RequiredProductForm';

const STEPS = {
  PRODUCT: 0,
  POSE: 1,
  BACKGROUND: 2,
  CROP_OPTION: 3,
};

const MultiConfig = ({ product }) => {
  const dispatch = useDispatch();
  const {
    allAvailablePosesForOrder,
    availablePoses,
    studentGallery,
    selectedStudent,
  } = useSelector((state) => state.additionalData);
  const { backgroundOptions } = useSelector((state) => state.shoot);
  const cropOptions = useSelector((state) => state.shoot?.crop_options || []);
  const cropOptionsEnabled = useSelector(
    (state) => state.shoot?.crop_options_enabled || false,
  );
  const smsNumberConfirmed = useSelector(
    (state) => state.v3Order.smsNumberConfirmed,
  );
  const { phone, smsPhone } = useSelector((state) => state.parent);
  const parent = useSelector((state) => state.parent);
  const shoot = useSelector((state) => state.shoot.shoot);
  const shippingRestricted = isShippingRestrictedForParent(shoot, parent);
  const currentUserId = useSelector((state) => state.auth.currentUserId);
  const [step, setStep] = useState(STEPS.PRODUCT);
  const [products, setProducts] = useState({}); // [id]: { product, pose, background, cropSelections }
  const [selectedProductId, setSelectedProductId] = useState(null);
  const isPrepay = useSelector((state) => state.shoot.isPrepay);
  const [backgroundsCategoryIndex, setBackgroundsCategoryIndex] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const sortedProducts = Object.values(products).sort(
    (a, b) => a.product.order - b.product.order,
  );
  const productRequiresSMS = product?.child_products?.some?.(
    (prod) => prod.sms_required,
  );
  const requiredBackgroundsCount = product?.child_products?.reduce(
    (acc, prod) => {
      return acc + prod.backgrounds_count;
    },
    0,
  );
  const requiredPosesCount = product?.child_products?.reduce((acc, prod) => {
    return acc + prod.poses_count;
  }, 0);
  const selectedBackgrounds = Object.values(products).reduce((acc, prod) => {
    if (prod.background) return [...acc, prod.background];
    return acc;
  }, []);
  const selectedPoses = Object.values(products).reduce((acc, prod) => {
    if (prod.pose) return [...acc, prod.pose];
    return acc;
  }, []);
  const [selectedProductOptionKey, setSelectedProductOptionKey] =
    useState(null);
  const [selectedProductOptions, setSelectedProductOptions] = useState({});
  const [
    productOptionsDefaultsInitializedFor,
    setProductOptionsDefaultsInitializedFor,
  ] = useState(null);
  const { control, handleSubmit, register, setValue } = useForm();

  useEffect(() => {
    if (step === STEPS.BACKGROUND && backgroundOptions.length === 0) {
      const childProduct = products[selectedProductId]?.product;
      if (childProduct && shouldShowCropOptions(childProduct)) {
        setStep(STEPS.CROP_OPTION);
      } else {
        setStep(STEPS.PRODUCT);
      }
    }
  }, [step]);

  const canBeSubmitted = () => {
    // Product options are optional - removed validation
    if (
      (selectedBackgrounds.length === requiredBackgroundsCount ||
        backgroundOptions.length === 0) &&
      (isPrepay || selectedPoses.length === requiredPosesCount)
    ) {
      // When crop options are enabled, ensure every child that needs them has complete selections
      if (cropOptionsEnabled && cropOptions.length > 0) {
        const allCropSelectionsComplete = Object.values(products).every(
          (prod) => {
            if (!shouldShowCropOptions(prod.product)) return true;
            const requiredCount = prod.product.poses_count || 1;
            const selectedCount = (prod.cropSelections || []).filter(
              Boolean,
            ).length;
            return selectedCount >= requiredCount;
          },
        );
        if (!allCropSelectionsComplete) return false;
      }
      return true;
    }
  };

  const handleConfirmSMS = async () => {
    const phoneNumberObject = parsePhoneNumberFromString(
      smsPhone || phone,
      'US',
    );

    if (!phoneNumberObject || !phoneNumberObject.isValid()) {
      Swal.fire('Invalid Phone Number', '', 'error');
      return;
    }
    const result = await Swal.fire({
      title: 'Please Confirm',
      text: `Please confirm ${phoneNumberObject.formatNational()} is the mobile number where we should send your SMS-based products`,
      confirmButtonText: 'OK',
      showCancelButton: true,
      reverseButtons: true,
      focusConfirm: true,
      cancelButtonText: 'Edit',
    });

    if (result.value === true) {
      dispatch(updateSMSNumberConfirmed(true));
      Swal.fire(
        'Confirmed',
        'You may now add the product to your cart',
        'success',
      );
    } else if (result.dismiss === Swal.DismissReason.cancel) {
      setEditMode(true);
    }
  };

  const handleProductClick = (product) => () => {
    setSelectedProductId(product.id);
    if (isPrepay) {
      setStep(STEPS.BACKGROUND);
    } else {
      setStep(STEPS.POSE);
    }
  };

  const shouldShowCropOptions = (childProduct) =>
    isPrepay &&
    cropOptionsEnabled &&
    childProduct.crop_options_enabled !== false &&
    childProduct.poses_count > 0 &&
    cropOptions.length > 0;

  useEffect(() => {
    if (product.child_products) {
      const newProducts = product.child_products.reduce((acc, prod) => {
        const { id } = prod;
        return {
          ...acc,
          [id]: {
            product: prod,
            background: null,
            pose: null,
            cropSelections: [],
          },
        };
      }, {});
      setProducts(newProducts);
    }
  }, [product]);

  useEffect(() => {
    const productOptionsDefaultsKey = getProductOptionsDefaultsKey(product);
    if (
      !productOptionsDefaultsKey ||
      productOptionsDefaultsInitializedFor === productOptionsDefaultsKey
    ) {
      return;
    }

    setSelectedProductOptions((currentOptions) =>
      reconcileProductOptions(product, currentOptions),
    );
    setProductOptionsDefaultsInitializedFor(productOptionsDefaultsKey);
  }, [product, productOptionsDefaultsInitializedFor]);

  const addProductToCart = () => {
    handleSubmit((formState) => {
      if (!smsNumberConfirmed && productRequiresSMS) {
        handleConfirmSMS();
      } else {
        const backgroundIds = [];
        const backgroundSelections = [];
        const poseIds = [];
        const poses = [];
        const childCropSelections = [];
        sortedProducts.forEach((prod) => {
          if (prod.background?.id) {
            backgroundIds.push(prod.background.id);
            if (product?.triggers_free_backgrounds) {
              prod.background.price = 0;
            }
            backgroundSelections.push(prod.background);
          }
          if (prod.pose) {
            poseIds.push(prod.pose.id);
            poses.push(prod.pose);
          }
          childCropSelections.push(prod.cropSelections?.filter(Boolean) || []);
        });

        dispatch(
          addToCart({
            studentId: selectedStudent.id,
            fullproductData: product,
            backgroundIds,
            backgroundSelections,
            poseIds,
            poses,
            quantity: 1,
            requiredFields: formState,
            productOptionKey: selectedProductOptionKey,
            productOptions: selectedProductOptions,
            childCropSelections,
          }),
        );
      }
    })();
  };

  const addBackgroundSelection = (background) => () => {
    const newProducts = { ...products };
    if (newProducts[selectedProductId]) {
      newProducts[selectedProductId].background = background;
    }
    setProducts(newProducts);

    const childProduct = newProducts[selectedProductId]?.product;
    if (childProduct && shouldShowCropOptions(childProduct)) {
      setStep(STEPS.CROP_OPTION);
    } else {
      setStep(STEPS.PRODUCT);
    }
  };

  const addPoseSelection = (pose) => {
    const newProducts = { ...products };
    if (newProducts[selectedProductId]) {
      newProducts[selectedProductId].pose = pose;
    }
    setProducts(newProducts);
    setStep(STEPS.BACKGROUND);
  };

  const handleCropSelectionsChange = (newSelections) => {
    const newProducts = { ...products };
    if (newProducts[selectedProductId]) {
      newProducts[selectedProductId].cropSelections = newSelections;
    }
    setProducts(newProducts);
  };

  const confirmCropSelection = () => {
    setStep(STEPS.PRODUCT);
  };

  const handleProductOptionChange = (optionSetKey, optionValue) => {
    setSelectedProductOptions((prev) => ({
      ...prev,
      [optionSetKey]: optionValue,
    }));
    // For legacy support, also set the single option key
    setSelectedProductOptionKey(optionValue);
  };

  const renderBackgroundCategoryTabs = () => {
    if (backgroundOptions.length > 1) {
      return backgroundOptions.map((backgroundCategory, index) => {
        let activeClass = '';

        if (index === backgroundsCategoryIndex) {
          activeClass = 'group-active';
        }
        return (
          <li
            key={backgroundCategory.id}
            className={`bnl-product-config__bg-selector__tab ${activeClass}`}
            onClick={() => setBackgroundsCategoryIndex(index)}
          >
            {backgroundCategory.name}
          </li>
        );
      });
    }
  };

  const renderBackgroundOptions = () => {
    return backgroundOptions[backgroundsCategoryIndex]?.background_images?.map(
      (background) => {
        const backgroundOptionStyle = {
          backgroundImage: `url(${background.image_thumbnail})`,
          backgroundPosition: 'center center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
        };

        const lastPose = products[selectedProductId]?.pose;
        // Don't layer JPG images over backgrounds - they don't have alpha transparency
        const shouldShowPoseOverlay =
          !isPrepay && lastPose && !isJpgImage(lastPose.url);

        return (
          <MultiConfigGridItem
            key={background.id}
            style={backgroundOptionStyle}
            onClick={addBackgroundSelection(background)}
          >
            {shouldShowPoseOverlay && (
              <img
                src={lastPose.url}
                onContextMenu={(e) => e.preventDefault()}
                alt="Overlay Pose"
              />
            )}
            <ProductItemButton>Choose Background</ProductItemButton>
          </MultiConfigGridItem>
        );
      },
    );
  };

  const renderPoses = () => {
    let poseSource =
      studentGallery && studentGallery?.length > 0
        ? studentGallery
        : availablePoses;

    if (allAvailablePosesForOrder?.length > 0) {
      poseSource = allAvailablePosesForOrder;
    }

    const poses = poseSource?.map((pose) => {
      // TODO: handle group image type
      return (
        <MultiConfigGridItem
          key={pose.id}
          onClick={() => {
            addPoseSelection(pose);
          }}
        >
          <img
            src={pose.url}
            onContextMenu={(event) => event.preventDefault()}
          />
          <ProductItemButton>Select & Continue</ProductItemButton>
        </MultiConfigGridItem>
      );
    });

    return poses;
  };

  const renderDirections = () => {
    const productNamePrice = `${product.name} (${formatUSD(
      product.price,
      true,
    )})`;
    const childProductName = products?.[selectedProductId]?.product?.name;
    const defaultDirections = (
      <MultiConfigSubHeader>
        {isPrepay
          ? `${productNamePrice}: Configure each of these products:`
          : `${productNamePrice}: Choose from any pose for each of these products:`}
      </MultiConfigSubHeader>
    );

    switch (step) {
      case STEPS.POSE:
        if (childProductName) {
          return (
            <>
              <MultiConfigSubHeader>{productNamePrice}</MultiConfigSubHeader>
              <MultiConfigSubHeader>{`${childProductName}: please select one of the poses below for this product.`}</MultiConfigSubHeader>
            </>
          );
        }
        return defaultDirections;
      case STEPS.BACKGROUND:
        if (childProductName) {
          return (
            <>
              <MultiConfigSubHeader>{productNamePrice}</MultiConfigSubHeader>
              <MultiConfigSubHeader>{`${childProductName}: please select one of the backgrounds below for this product.`}</MultiConfigSubHeader>
            </>
          );
        }
        return defaultDirections;
      case STEPS.CROP_OPTION:
        if (childProductName) {
          return (
            <>
              <MultiConfigSubHeader>{productNamePrice}</MultiConfigSubHeader>
              <MultiConfigSubHeader>{`${childProductName}: select a crop style for this product.`}</MultiConfigSubHeader>
            </>
          );
        }
        return defaultDirections;
      case STEPS.PRODUCT:
      default:
        return defaultDirections;
    }
  };

  if (shippingRestricted && !isProductAllowed(product, true)) {
    return <Redirect to="/v3/offers" />;
  }

  return (
    <div>
      <MultiConfigHeader>
        {isPrepay
          ? 'Configure each item'
          : 'Choose your preferred photo for each item'}
      </MultiConfigHeader>
      {renderDirections()}
      {step === STEPS.BACKGROUND && backgroundOptions?.length > 1 && (
        <ul className="bnl-product-config__bg-selector__tabs-container">
          {renderBackgroundCategoryTabs()}
        </ul>
      )}
      <MultiConfigGrid>
        {step === STEPS.PRODUCT && (
          <>
            {sortedProducts.map((prod) => (
              <MultiConfigProductItem
                key={prod.id}
                item={prod}
                onClick={handleProductClick}
              />
            ))}
          </>
        )}
        {step === STEPS.POSE && renderPoses()}
        {step === STEPS.BACKGROUND && renderBackgroundOptions()}
      </MultiConfigGrid>
      {step === STEPS.CROP_OPTION && (
        <>
          <CropOptionsPrepaySelector
            cropOptions={cropOptions}
            posesCount={products[selectedProductId]?.product?.poses_count || 1}
            cropSelections={products[selectedProductId]?.cropSelections || []}
            onCropSelectionsChange={handleCropSelectionsChange}
          />
          <MultiConfigButton
            onClick={confirmCropSelection}
            disabled={
              (products[selectedProductId]?.cropSelections || []).filter(
                Boolean,
              ).length <
              (products[selectedProductId]?.product?.poses_count || 1)
            }
          >
            Confirm Crop Style
          </MultiConfigButton>
        </>
      )}
      {canBeSubmitted() && productRequiresSMS && (
        <SMSNumberConfirmation
          currentUserId={currentUserId}
          editMode={editMode}
          setEditMode={setEditMode}
          smsNumber={smsPhone}
          phone={phone}
          smsNumberConfirmed={smsNumberConfirmed}
        />
      )}
      {canBeSubmitted() && (
        <MultiConfigButton onClick={addProductToCart}>
          Done, continue
        </MultiConfigButton>
      )}
      <ProductOptionsSelector
        product={product}
        selectedOptions={selectedProductOptions}
        onOptionChange={handleProductOptionChange}
      />
      <RequiredProductForm
        product={product}
        register={register}
        control={control}
        setValue={setValue}
      />
    </div>
  );
};

export default MultiConfig;
